import { FlightDeckClient } from '@atlassiansox/flight-deck-frontend-client';

export interface I18nLabel {
  defaultMessage?: string;
  description?: string;
  id: string;
}

/* eslint-disable @atlaskit/design-system/ensure-design-token-usage */
export interface ProductIcons {
  blue: string;
  neutral: string;
  white: string;
}
/* eslint-enable @atlaskit/design-system/ensure-design-token-usage */

export interface ProductConfigurationApiBase {
  description: string | I18nLabel | null;
  href: string;
  key: string;
  label: string | I18nLabel;
  ordinal: number;
}

export type ProductConfigurationApiProduct = ProductConfigurationApiBase & {
  icons: ProductIcons;
};

export type ProductConfigurationApiLink = ProductConfigurationApiBase & {
  iconUrl: string;
};

export interface ProductConfigurationResponse {
  links: Record<string, ProductConfigurationApiLink>;
  products: Record<string, ProductConfigurationApiProduct>;
}

export interface FlightDeckArtifactProduct {
  apsKey: string;
  displayName: string;
  icons: {
    blue: string;
    neutral: string;
    white: string;
  };
  ordinal: number;
  siteUrlSuffix?: string;
  subtitle: FlightDeckI18nLabel | null;
  tcsProductKey?: string;
  tenantlessProductAri?: string;
  tenantlessUrl?: string;
  visibilityFeatureFlagName?: string;
}

export interface FlightDeckArtifactLink {
  apsKey: string;
  displayName: FlightDeckI18nLabel;
  iconUrl: string;
  ordinal: number;
  subtitle: FlightDeckI18nLabel | null;
  url: string;
  visibilityFeatureFlagName?: string;
}

export interface FlightDeckProductConfigurationArtifact {
  links: FlightDeckArtifactLink[];
  products: FlightDeckArtifactProduct[];
}

export interface FlightDeckI18nLabel {
  defaultMessage: string;
  description: string;
  id: string;
}

const transformFlightDeckArtifactProductsToProductConfigurationProduct = (
  artifactProduct: FlightDeckArtifactProduct,
): ProductConfigurationApiProduct => {
  return {
    key: artifactProduct.apsKey,
    href: artifactProduct.siteUrlSuffix || '',
    label: artifactProduct.displayName,
    description: artifactProduct.subtitle || null,
    icons: {
      blue: artifactProduct.icons.blue,
      neutral: artifactProduct.icons.neutral,
      white: artifactProduct.icons.white,
    },
    ordinal: artifactProduct.ordinal,
  };
};

const transformFlightDeckArtifactLinkToProductConfigurationLink = (
  artifactLink: FlightDeckArtifactLink,
): ProductConfigurationApiLink => {
  return {
    key: artifactLink.apsKey,
    href: artifactLink.url,
    label: artifactLink.displayName,
    description: artifactLink.subtitle || null,
    iconUrl: artifactLink.iconUrl,
    ordinal: artifactLink.ordinal,
  };
};

export const transformFlightDeckArtifactToProductConfiguration = (
  artifact: FlightDeckProductConfigurationArtifact,
): ProductConfigurationResponse => ({
  products: artifact.products.reduce<Record<string, ProductConfigurationApiProduct>>((acc, val) => {
    acc[val.apsKey] = transformFlightDeckArtifactProductsToProductConfigurationProduct(val);
    return acc;
  }, {}),
  links: artifact.links.reduce<Record<string, ProductConfigurationApiLink>>((acc, val) => {
    acc[val.apsKey] = transformFlightDeckArtifactLinkToProductConfigurationLink(val);
    return acc;
  }, {}),
});

export const resolvedFlightDeckClient = new FlightDeckClient('start');
